import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdbModalRef, MdbModalService } from 'mdb-angular-ui-kit/modal';
import { MdbPopconfirmRef, MdbPopconfirmService } from 'mdb-angular-ui-kit/popconfirm';
import { finalize, Subscription } from 'rxjs';
import { END_USER_STATUS, MESSAGE_CONSTANTS, NIGHTWEB_APP_CONSTANT, USER_STATUS } from 'src/app/constants';
import { DataService, ToasterService, UserService } from 'src/app/services';
import { DateUtility, NightwebUtil } from 'src/app/utils';
import { PopConfirmComponent } from '../common';
import { TableConfig } from '../common/interface';
import { AddEditUserModalComponent, ConfigurationModalComponent, ConfirmationModalComponent, LinkDeviceModalComponent } from '../modals';

@Component({
  selector: 'app-end-user',
  templateUrl: './end-user.component.html',
  styleUrls: ['./end-user.component.scss']
})
export class EndUserComponent implements OnInit {

  endUserForm!: FormGroup;

  loading: boolean = false;
  submitted: boolean = false;
  userStatus = USER_STATUS;
  endUsers: any[] = [];
  searchKeyword: string = '';
  userStatusList = END_USER_STATUS;
  phoneMask = NIGHTWEB_APP_CONSTANT.phoneMask
  config = NIGHTWEB_APP_CONSTANT.modalConfig;
  confirmPopConfig = NIGHTWEB_APP_CONSTANT.confirmPopConfig;
  userAddEditComponent!: MdbModalRef<AddEditUserModalComponent>;
  linkDeviceComponent!: MdbModalRef<LinkDeviceModalComponent>;
  configurationModalConfig!: MdbModalRef<ConfigurationModalComponent>;
  confirmationModalRef!: MdbModalRef<ConfirmationModalComponent>;
  popconfirmRef: MdbPopconfirmRef<PopConfirmComponent> | null = null;
  limits = NIGHTWEB_APP_CONSTANT.showLimits;
  defaultDateFormat = NIGHTWEB_APP_CONSTANT.defaultDateFormat;
  Object = Object;
  isWriteAccessible: boolean = this.nightWebUtil.isAccessible('End Users', 'W');
  isSuperAdmin: boolean = this.nightWebUtil.isSuperAdmin();
  filter: any;

  tableConfig: TableConfig = {
    headerElements: [
      'Name', 'Contact', 'Address', 'Link Device', 'Status', 'Last Updated', ''
    ],
    bodyElements: [
      { value: 'name', navigation: true },
      { value: 'contact', navigation: true },
      { value: 'address', dataType: 'address', navigation: true },
      { value: '', actionButton: true },
    ],
    tableDatas: [],
    actionItems: [
      { name: 'Edit', icon: 'pencil-square', eventName: 'editModule' },
      { name: 'Delete', icon: 'trash', eventName: 'deleteModule' }
    ]
  };
  dateFormat = NIGHTWEB_APP_CONSTANT.mdbDefaultDateFormat;
  subscription!: Subscription;
  constructor(
    private dataService: DataService,
    private formBuilder: FormBuilder,
    private dateUtility: DateUtility,
    private userService: UserService,
    private nightWebUtil: NightwebUtil,
    private modalService: MdbModalService,
    private toasterService: ToasterService,
    private popconfirmService: MdbPopconfirmService
  ) {
    this.setEndUserForm();
  }

  ngOnInit(): void {
    this.getAllEndUsers();

    this.dataService.getData().subscribe(
      (data) => {
        if (data) {
          this.getAllEndUsers();
        }
      }
    )
  }

  setEndUserForm() {
    this.endUserForm = this.formBuilder.group({
      firstName: [''],
      lastName: [''],
      phone: [''],
      email: ['', Validators.pattern(NIGHTWEB_APP_CONSTANT.emailPattern)],
      status: [''],
      keyword: [''],
      sourceOfRequest: [''],
      createdFrom: [''],
      createdTo: [''],
      limit: [NIGHTWEB_APP_CONSTANT.defaultLimit],
      offset: []
    });
  }

  getAllEndUsers(filter?: any) {
    this.loading = true;
    this.endUsers = [];
    this.userService.getAllEndUsers(filter)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (response) => {
          this.endUsers = response.data.rows;
          // map address to object of object if the response is in array of objects
          this.endUsers = response.data.rows.map((user: any) => {
            if (user?.demographic?.address.length) {
              const [address] = user?.demographic?.address;
              user.demographic.address = {
                home: address
              }
            }
            return user;
          });
        },
        error: (e) => {
        }
      })
  }

  openAddEditUserModal(user?: any) {
    const config = this.nightWebUtil.shallowCopy(this.config);
    config.data.title = user ? 'Edit' : 'Add';
    config.modalClass = 'modal-dialog-scrollable modal-md';
    config.data.formData = user;
    this.userAddEditComponent = this.modalService.open(AddEditUserModalComponent, config);
    this.userAddEditComponent.onClose.subscribe((data: any) => {
      if (data) {
        this.getAllEndUsers();
      }
    });
  }

  openLinkDeviceModal(user?: any) {
    const config = this.nightWebUtil.shallowCopy(this.config);
    config.data.title = 'Link Device';
    config.modalClass = 'modal-dialog-scrollable modal-md';
    config.data.formData = user;
    config.data.formData.userId = user.id;
    config.data.formData.configurations = user.configurations;
    config.data.confirmButtonLabel = 'Link Device';
    this.linkDeviceComponent = this.modalService.open(LinkDeviceModalComponent, config);
    this.linkDeviceComponent.onClose.subscribe((data: any) => {
      if (data) {
        this.getAllEndUsers();
      }
    });
  }

  openUnlinkDeviceModal(user?: any) {
    const config = this.nightWebUtil.shallowCopy(this.config);
    config.data.confirmButtonLabel = 'Unlink';
    config.data.formData = user;
    config.data.title = `Unlink Device`;
    config.data.body = `Are you sure you want to unlink <strong>${user?.demographic?.firstName + ' ' + user?.demographic?.middleName + ' ' + user?.demographic?.lastName}</strong>?`;
    this.confirmationModalRef = this.modalService.open(ConfirmationModalComponent, config);
    this.confirmationModalRef.onClose.subscribe((data: any) => {
      if (data) {
        const unlink = {
          deviceId: data?.activeDevice?.deviceId,
          endUserId: data?.id,
          reason: ''
        }
        this.unlinkDevice(unlink);
      }
    });
  }

  openEditConfigurationModal(user?: any) {
    const config = this.nightWebUtil.shallowCopy(this.config);
    config.data.title = 'Configuration';
    config.modalClass = 'modal-dialog-scrollable modal-md';
    config.data.formData = user;
    config.data.formData.mode = 'edit';
    config.data.confirmButtonLabel = 'Update';
    config.data.formData.userId = user.id;
    config.data.formData.module = 'user';
    config.data.formData.deviceId = user.activeDevice.deviceId;
    this.configurationModalConfig = this.modalService.open(ConfigurationModalComponent, config);
    this.configurationModalConfig.onClose.subscribe((data: any) => {
      if (data) {
        this.getAllEndUsers();
      }
    });
  }

  unlinkDevice(data: any) {
    this.userService.unLinkDevice(data)
      .subscribe({
        next: (response) => {
          this.getAllEndUsers();
          this.toasterService.show('Success', MESSAGE_CONSTANTS.DEVICE_UNLINKED);
        },
        error: (e) => {
        }
      })
  }

  deleteUser(event: Event, user: any) {
    const target = event.target as HTMLElement;
    const config = this.nightWebUtil.shallowCopy(this.confirmPopConfig);
    config.data.confirmButtonLabel = 'Delete';
    config.data.title = `Delete`;
    config.data.body = MESSAGE_CONSTANTS.DELETE_CONFIRMATION;
    this.popconfirmRef = this.popconfirmService.open(PopConfirmComponent, target, config);
    this.popconfirmRef.onConfirm.subscribe(() => {
      this.userService.delete(user.id)
        .subscribe({
          next: (response) => {
            this.endUsers.splice(this.endUsers.findIndex(x => x.id == user.id), 1);
            this.toasterService.show("Success", MESSAGE_CONSTANTS.USER_REMOVED);
          },
          error: (e) => {
          }
        })
    });
  }

  search() {
    if (this.endUserForm.valid) {
      this.filter = this.nightWebUtil.shallowCopy(this.endUserForm.value);
      this.filter.createdFrom = this.dateUtility.formatDate(this.filter.createdFrom);
      this.filter.createdTo = this.dateUtility.formatDate(this.filter.createdTo);
      this.filter.phone = this.filter.phone.toString();
      this.filter = this.nightWebUtil.getFilterParams(this.filter);
      this.getAllEndUsers(this.filter);
    }
  }

  clear() {
    this.setEndUserForm();
    this.filter = this.nightWebUtil.getFilterParams(this.endUserForm.value)
    this.getAllEndUsers(this.filter);
  }

  getMaxDate() {
    return this.dateUtility.getMaxCreatedFromDate();
  }

  getMinDate() {
    return this.dateUtility.getMinCreatedToDate(this.endUserForm.controls['createdFrom']?.value);
  }

  getFiltered() {
    let filterParams: any = this.nightWebUtil.getFilterParams(this.endUserForm.value);
    if (filterParams?.createdFrom) {
      filterParams.createdFrom = this.dateUtility.convertToMMddYYYY(filterParams?.createdFrom);
    }
    if (filterParams?.createdTo) {
      filterParams.createdTo = this.dateUtility.convertToMMddYYYY(filterParams?.createdTo);
    }
    this.getAllEndUsers(filterParams);
  }

  receiveSelectedValue(event: any) {
    if (event) {
      this.endUserForm.patchValue({
        [event.controlName]: event.value
      });
      if (event.controlName === 'limit') {
        this.filter = this.nightWebUtil.getFilterParams(this.endUserForm.value)
        this.getAllEndUsers(this.filter);
      }
    }
  }

  receiveEvent({ action, event, object }: any) {
    if (action === 'edit') {
      this.openAddEditUserModal(object);
    } else if (action === 'delete') {
      this.deleteUser(event, object);
    }
  }

  receiveSearchKeyword(keyword: any) {
    this.searchKeyword = keyword;
    this.endUserForm.patchValue({ keyword });
    this.filter = this.nightWebUtil.getFilterParams(this.endUserForm.value)
    this.getAllEndUsers(this.filter);
  }
}
